import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { shoppingApi, type ShoppingSessionItem, type PickStatus } from '../api/shopping';
import { itemsApi } from '../api/items';
import { PageHeader } from '../components/ui/PageHeader';
import { FAB } from '../components/ui/FAB';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonList } from '../components/ui/Skeleton';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { useToast } from '../components/ui/Toast';
import { useOnline } from '../hooks/useOnline';
import { enqueuePickUpdate } from '../store/offlineQueue';
import { formatCurrency, parseCurrencyInput, normalizeText } from '../utils/format';
import { PlusIcon, CheckIcon, XIcon } from '../components/ui/icons';

interface AddItemPayload {
  itemName: string;
  quantityToBuy: string;
  unitPrice?: string | null;
  brand?: string;
}

function AddItemModal({
  isOpen,
  onClose,
  sessionId,
}: {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
}) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [form, setForm] = useState<AddItemPayload>({ itemName: '', quantityToBuy: '1', unitPrice: '', brand: '' });
  const [errors, setErrors] = useState<Partial<AddItemPayload>>({});
  const [brandTouched, setBrandTouched] = useState(false);
  const [brandHint, setBrandHint] = useState<string | null>(null);

  // As the user types the item name, look up whether it matches an existing
  // household item and, if so, suggest the last brand bought for it — still
  // fully editable, per product requirement.
  useEffect(() => {
    const name = form.itemName.trim();
    if (!name || brandTouched) {
      setBrandHint(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const matches = await itemsApi.list({ q: name });
        if (cancelled) return;
        const exact = matches.find((m) => normalizeText(m.name) === normalizeText(name));
        if (exact?.lastBrand) {
          setForm((f) => (f.itemName.trim() === name ? { ...f, brand: exact.lastBrand! } : f));
          setBrandHint(exact.lastBrand);
        } else {
          setBrandHint(null);
        }
      } catch {
        // Best-effort suggestion — ignore failures silently.
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.itemName, brandTouched]);

  const mutation = useMutation({
    mutationFn: (payload: AddItemPayload) =>
      shoppingApi.addItemDuringShopping(sessionId, {
        itemName: payload.itemName,
        quantityToBuy: payload.quantityToBuy,
        unitPrice: payload.unitPrice ? parseCurrencyInput(payload.unitPrice) || null : null,
        brand: payload.brand?.trim() || null,
      }),
    onSuccess: (newItem) => {
      queryClient.setQueryData<ShoppingSessionItem[]>(['shopping', sessionId, 'items'], (old) =>
        old ? [...old, newItem] : [newItem]
      );
      addToast(`"${newItem.itemNameSnapshot}" adicionado!`, 'success');
      setForm({ itemName: '', quantityToBuy: '1', unitPrice: '', brand: '' });
      setBrandTouched(false);
      setBrandHint(null);
      onClose();
    },
    onError: () => addToast('Erro ao adicionar item.', 'error'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Partial<AddItemPayload> = {};
    if (!form.itemName.trim()) newErrors.itemName = 'Informe o nome';
    if (!form.quantityToBuy.trim()) newErrors.quantityToBuy = 'Informe a quantidade';
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }
    mutation.mutate(form);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Adicionar item à compra">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Nome do item"
          placeholder="ex: Iogurte, Biscoito"
          value={form.itemName}
          onChange={(e) => setForm((f) => ({ ...f, itemName: e.target.value }))}
          error={errors.itemName}
          name="itemName"
          autoFocus
        />
        <Input
          label="Quantidade"
          placeholder="ex: 2, 1kg"
          value={form.quantityToBuy}
          onChange={(e) => setForm((f) => ({ ...f, quantityToBuy: e.target.value }))}
          error={errors.quantityToBuy}
          name="quantityToBuy"
        />
        <Input
          label="Marca (opcional)"
          placeholder="ex: Nestlé, Colgate"
          value={form.brand ?? ''}
          onChange={(e) => { setForm((f) => ({ ...f, brand: e.target.value })); setBrandTouched(true); }}
          name="brand"
          hint={brandHint ? `Última marca comprada: ${brandHint}` : undefined}
        />
        <Input
          label="Preço (opcional)"
          placeholder="0,00"
          type="text"
          inputMode="decimal"
          prefix="R$"
          value={form.unitPrice ?? ''}
          onChange={(e) => setForm((f) => ({ ...f, unitPrice: e.target.value }))}
          name="unitPrice"
        />
        <Button type="submit" variant="primary" fullWidth size="md" loading={mutation.isPending} title="Adicionar" aria-label="Adicionar item à compra">
          <PlusIcon size={16} />
        </Button>
      </form>
    </Modal>
  );
}

const STATUS_BORDER: Record<PickStatus, string> = {
  TO_PICK: 'border-l-emerald-500',
  PICKED: 'border-l-green-400',
  SKIPPED: 'border-l-gray-300',
  NOT_FOUND: 'border-l-red-400',
};

const STATUS_BG: Record<PickStatus, string> = {
  TO_PICK: 'bg-white',
  PICKED: 'bg-green-50 opacity-80',
  SKIPPED: 'bg-gray-50 opacity-70',
  NOT_FOUND: 'bg-red-50 opacity-70',
};

interface SessionItemCardProps {
  item: ShoppingSessionItem;
  isExpanded: boolean;
  onToggle: () => void;
  onPick: (price?: string) => void;
  onSkip: () => void;
  onNotFound: () => void;
  onUndo: () => void;
  isUpdating: boolean;
}

function SessionItemCard({
  item,
  isExpanded,
  onToggle,
  onPick,
  onSkip,
  onNotFound,
  onUndo,
  isUpdating,
}: SessionItemCardProps) {
  const [priceInput, setPriceInput] = useState(item.lastUnitPrice ?? '');
  const isDone = item.pickStatus !== 'TO_PICK';

  return (
    <div
      className={[
        'rounded-2xl border-l-4 shadow-sm overflow-hidden transition-all',
        STATUS_BORDER[item.pickStatus],
        STATUS_BG[item.pickStatus],
      ].join(' ')}
    >
      {/* Card header - always visible */}
      <div
        className={`p-4 ${!isDone ? 'cursor-pointer' : ''}`}
        onClick={() => !isDone && onToggle()}
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <p
              className={`font-bold text-base ${
                item.pickStatus === 'SKIPPED' ? 'line-through text-gray-400' : 'text-gray-900'
              }`}
            >
              {item.itemNameSnapshot}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-1">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                {item.quantityToBuy}
              </span>
              {item.pickStatus === 'PICKED' && item.unitPrice && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  {formatCurrency(item.unitPrice)}
                </span>
              )}
            </div>
            {item.lastStoreName && item.lastUnitPrice && item.pickStatus === 'TO_PICK' && (
              <p className="text-xs text-gray-400 mt-1">
                Última vez: {formatCurrency(item.lastUnitPrice)} em {item.lastStoreName}
              </p>
            )}
          </div>

          {isDone ? (
            <button
              onClick={(e) => { e.stopPropagation(); onUndo(); }}
              className="text-xs text-gray-400 hover:text-gray-600 font-medium py-1 px-2 rounded touch-manipulation"
            >
              Desfazer
            </button>
          ) : (
            <svg
              width="16"
              height="16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              className={`text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </div>

      {/* Expanded actions */}
      {isExpanded && !isDone && (
        <div className="border-t border-gray-100 p-4 space-y-3">
          {/* Price input */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 flex-shrink-0">Preço:</label>
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">R$</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder={item.lastUnitPrice ? formatCurrency(item.lastUnitPrice) : '0,00'}
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                className="w-full h-11 pl-9 pr-3 rounded-xl border border-gray-300 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          {/* Main action */}
          <button
            onClick={() => onPick(priceInput ? parseCurrencyInput(priceInput) : undefined)}
            disabled={isUpdating}
            className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white font-bold text-lg rounded-xl transition-colors touch-manipulation disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Peguei!
          </button>

          {/* Secondary actions */}
          <div className="flex gap-2">
            <button
              onClick={onSkip}
              disabled={isUpdating}
              className="flex-1 h-11 border border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors touch-manipulation"
            >
              Pular
            </button>
            <button
              onClick={onNotFound}
              disabled={isUpdating}
              className="flex-1 h-11 border border-red-300 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 transition-colors touch-manipulation"
            >
              Não encontrei
            </button>
          </div>

          {/* Close expand */}
          <button
            onClick={onToggle}
            className="w-full text-xs text-gray-400 py-1 hover:text-gray-600 touch-manipulation"
          >
            Fechar
          </button>
        </div>
      )}
    </div>
  );
}

export function ActiveSessionScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const online = useOnline();

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [showConfirmFinishModal, setShowConfirmFinishModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showStockReviewModal, setShowStockReviewModal] = useState(false);
  const [stockInputs, setStockInputs] = useState<Record<string, string>>({});

  const { data: session, isLoading: sessionLoading } = useQuery({
    queryKey: ['shopping', 'active'],
    queryFn: () => shoppingApi.getActiveSession(),
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });

  const { data: sessionItems, isLoading: itemsLoading, refetch } = useQuery({
    queryKey: ['shopping', session?.id, 'items'],
    queryFn: () => shoppingApi.getSessionItems(session!.id),
    enabled: Boolean(session?.id),
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });

  const updatePickMutation = useMutation({
    mutationFn: async ({
      itemId,
      status,
      price,
      quantityAtHome,
    }: {
      itemId: string;
      status: PickStatus;
      price?: string;
      quantityAtHome?: string;
    }) => {
      // ADR 014 — offline: queue the action and replay it when back online.
      if (!navigator.onLine) {
        await enqueuePickUpdate({
          sessionId: session!.id,
          itemId,
          pickStatus: status,
          unitPrice: price ?? null,
        });
        return null;
      }
      return shoppingApi.updatePickStatus(session!.id, itemId, {
        pickStatus: status,
        unitPrice: price || null,
        ...(quantityAtHome !== undefined ? { quantityAtHome } : {}),
      });
    },
    onMutate: async ({ itemId, status, price }) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: ['shopping', session?.id, 'items'] });
      const prev = queryClient.getQueryData<ShoppingSessionItem[]>([
        'shopping',
        session?.id,
        'items',
      ]);
      queryClient.setQueryData<ShoppingSessionItem[]>(
        ['shopping', session?.id, 'items'],
        (old) =>
          old?.map((i) =>
            i.id === itemId
              ? { ...i, pickStatus: status, unitPrice: price ?? i.unitPrice }
              : i
          )
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(['shopping', session?.id, 'items'], ctx.prev);
      }
      addToast('Erro ao atualizar item. Revertendo.', 'error');
    },
    onSuccess: () => {
      setExpandedId(null);
    },
  });

  const finishMutation = useMutation({
    mutationFn: () => shoppingApi.finishSession(session!.id),
    onSuccess: (finished) => {
      queryClient.setQueryData(['shopping', 'active'], null);
      queryClient.invalidateQueries({ queryKey: ['shopping'] });
      if (finished.status === 'CANCELLED') {
        // Nothing was actually picked — the backend cancels instead of finishing.
        navigate('/shopping');
        addToast('Nenhum item foi comprado. Compra cancelada.', 'info');
      } else {
        navigate('/history');
        addToast('Compra finalizada!', 'success');
      }
    },
    onError: () => addToast('Erro ao finalizar compra.', 'error'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => shoppingApi.cancelSession(session!.id),
    onSuccess: () => {
      queryClient.setQueryData(['shopping', 'active'], null);
      queryClient.invalidateQueries({ queryKey: ['shopping'] });
      navigate('/shopping');
      addToast('Compra cancelada.', 'info');
    },
    onError: () => addToast('Erro ao cancelar compra.', 'error'),
  });

  const handlePick = useCallback(
    (itemId: string, rawPrice?: string) => {
      const price = rawPrice ? parseCurrencyInput(rawPrice) : undefined;
      updatePickMutation.mutate({ itemId, status: 'PICKED', price });
    },
    [updatePickMutation]
  );

  const handleSkip = useCallback(
    (itemId: string) => {
      updatePickMutation.mutate({ itemId, status: 'SKIPPED' });
    },
    [updatePickMutation]
  );

  const handleNotFound = useCallback(
    (itemId: string) => {
      updatePickMutation.mutate({ itemId, status: 'NOT_FOUND' });
    },
    [updatePickMutation]
  );

  const handleUndo = useCallback(
    (itemId: string) => {
      updatePickMutation.mutate({ itemId, status: 'TO_PICK' });
    },
    [updatePickMutation]
  );

  const handleFinishClick = () => {
    setShowConfirmFinishModal(true);
  };

  // ADR 031 — suggest the new home stock: snapshot + quantity, only when both
  // are plain numbers (quantities are free text per ADR 007).
  function suggestStock(quantityAtHomeSnapshot: string | null, quantityToBuy: string): string {
    const num = (s: string | null) => {
      const t = (s ?? '').trim();
      return /^\d+([.,]\d+)?$/.test(t) ? parseFloat(t.replace(',', '.')) : null;
    };
    const a = num(quantityAtHomeSnapshot);
    const b = num(quantityToBuy);
    if (a !== null && b !== null) return String(Math.round((a + b) * 100) / 100);
    return quantityAtHomeSnapshot ?? '';
  }

  const openStockReview = (picked: ShoppingSessionItem[]) => {
    const inputs: Record<string, string> = {};
    for (const item of picked) {
      if (item.householdItemId) {
        inputs[item.id] = suggestStock(item.quantityAtHomeSnapshot, item.quantityToBuy);
      }
    }
    setStockInputs(inputs);
    setShowStockReviewModal(true);
  };

  const handleConfirmFinish = () => {
    setShowConfirmFinishModal(false);
    const picked = (sessionItems ?? []).filter((i) => i.pickStatus === 'PICKED');
    if (picked.length === 0) return;
    // ADR 031 — stock review comes before closing the session.
    openStockReview(picked);
  };

  const handleStockReviewConfirm = async () => {
    setShowStockReviewModal(false);
    const picked = (sessionItems ?? []).filter((i) => i.pickStatus === 'PICKED');
    for (const item of picked) {
      const value = stockInputs[item.id]?.trim();
      if (value && item.householdItemId) {
        await updatePickMutation.mutateAsync({
          itemId: item.id,
          status: 'PICKED',
          quantityAtHome: value,
        });
      }
    }
    const itemsWithoutPrice = picked.filter((i) => !i.unitPrice);
    if (itemsWithoutPrice.length > 0) {
      setShowFinishModal(true);
      return;
    }
    finishMutation.mutate();
  };

  if (sessionLoading || itemsLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <PageHeader title="Modo Mercado" showBack />
        <div className="flex-1 px-4 py-4">
          <SkeletonList count={5} />
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex flex-col">
        <PageHeader title="Modo Mercado" showBack />
        <EmptyState
          title="Nenhuma compra em andamento"
          action={{ label: 'Iniciar compra', onClick: () => navigate('/shopping'), icon: <PlusIcon size={16} /> }}
        />
      </div>
    );
  }

  const items = sessionItems ?? [];
  const pending = items.filter((i) => i.pickStatus === 'TO_PICK');
  const done = items.filter((i) => i.pickStatus !== 'TO_PICK');
  const pickedWithPrice = items.filter((i) => i.pickStatus === 'PICKED' && i.unitPrice);
  const total = pickedWithPrice.reduce((sum, i) => sum + parseFloat(i.unitPrice!), 0);
  const withoutPrice = items.filter((i) => i.pickStatus === 'PICKED' && !i.unitPrice).length;
  const pickedCount = items.filter((i) => i.pickStatus === 'PICKED').length;

  return (
    <div className="flex flex-col min-h-screen pb-[calc(6rem+env(safe-area-inset-bottom))]">
      {/* Sticky header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">
              Modo Mercado
            </p>
            <p className="font-bold text-gray-900 text-lg leading-tight">{session.storeName}</p>
            <p className="text-sm text-gray-500">
              {done.length}/{items.length} itens &nbsp;&middot;&nbsp;
              {total > 0
                ? `R$ ${total.toFixed(2).replace('.', ',')}`
                : 'sem total ainda'}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {!online && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 mr-1">
                Offline
              </span>
            )}
            <button
              onClick={() => navigate('/chat')}
              className="w-10 h-10 flex items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 touch-manipulation"
              aria-label="Chat da compra"
              title="Chat da compra"
            >
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </button>
            <button
              onClick={() => refetch()}
              className="w-10 h-10 flex items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 touch-manipulation"
              aria-label="Atualizar"
            >
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              onClick={() => setShowCancelModal(true)}
              disabled={cancelMutation.isPending}
              className="w-10 h-10 flex items-center justify-center rounded-xl text-red-500 hover:bg-red-50 touch-manipulation disabled:opacity-50"
              aria-label="Cancelar compra"
            >
              <XIcon size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Items list */}
      <div className="flex-1 px-4 py-4 space-y-2">
        {items.length === 0 && (
          <EmptyState
            title="Nenhum item na lista"
            description="Use o botão + para adicionar itens."
          />
        )}

        {/* Pending */}
        {pending.map((item) => (
          <SessionItemCard
            key={item.id}
            item={item}
            isExpanded={expandedId === item.id}
            onToggle={() => setExpandedId((prev) => (prev === item.id ? null : item.id))}
            onPick={(price) => handlePick(item.id, price)}
            onSkip={() => handleSkip(item.id)}
            onNotFound={() => handleNotFound(item.id)}
            onUndo={() => handleUndo(item.id)}
            isUpdating={updatePickMutation.isPending}
          />
        ))}

        {/* Done items (dimmed) */}
        {done.length > 0 && (
          <>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider pt-2 px-1">
              Concluídos
            </p>
            {done.map((item) => (
              <SessionItemCard
                key={item.id}
                item={item}
                isExpanded={false}
                onToggle={() => {}}
                onPick={() => {}}
                onSkip={() => {}}
                onNotFound={() => {}}
                onUndo={() => handleUndo(item.id)}
                isUpdating={updatePickMutation.isPending}
              />
            ))}
          </>
        )}
      </div>

      {/* Bottom bar — above BottomNav */}
      <div className="fixed bottom-0 left-0 right-0 z-[60] pointer-events-none">
        <div
          className="mx-auto w-full max-w-md bg-white border-t border-gray-200 px-4 py-3 pointer-events-auto space-y-2"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
        >
        <Button
          variant="primary"
          fullWidth
          size="md"
          onClick={handleFinishClick}
          disabled={pickedCount === 0}
          loading={finishMutation.isPending}
          title={`Finalizar compra${withoutPrice > 0 ? ` (${withoutPrice} sem preço)` : ''}`}
          aria-label="Finalizar compra"
        >
          <CheckIcon size={16} />
          {withoutPrice > 0 && (
            <span className="ml-2 bg-amber-400 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {withoutPrice}
            </span>
          )}
        </Button>
        {pickedCount === 0 && (
          <p className="text-xs text-gray-400 text-center">
            Marque pelo menos um item como "Peguei!" para finalizar.
          </p>
        )}
        </div>
      </div>

      {/* FAB */}
      <FAB onClick={() => setShowAddModal(true)} aria-label="Adicionar item" bottomOffsetRem={pickedCount === 0 ? 3.25 : 2.25} />

      {/* Add item modal */}
      {session && (
        <AddItemModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          sessionId={session.id}
        />
      )}

      <ConfirmModal
        isOpen={showConfirmFinishModal}
        onClose={() => setShowConfirmFinishModal(false)}
        onConfirm={handleConfirmFinish}
        title="Finalizar compra"
        message={`Finalizar a compra em ${session.storeName}? ${pickedCount} ${pickedCount === 1 ? 'item comprado' : 'itens comprados'}${total > 0 ? ` · R$ ${total.toFixed(2).replace('.', ',')}` : ''}.`}
        confirmLabel="Finalizar"
        loading={finishMutation.isPending}
      />

      <ConfirmModal
        isOpen={showFinishModal}
        onClose={() => setShowFinishModal(false)}
        onConfirm={() => {
          setShowFinishModal(false);
          finishMutation.mutate();
        }}
        title="Itens sem preço"
        message={`${(sessionItems?.filter((i) => i.pickStatus === 'PICKED' && !i.unitPrice).length ?? 0)} item(ns) sem preço. Finalizar mesmo assim?`}
        confirmLabel="Finalizar mesmo assim"
        loading={finishMutation.isPending}
      />

      {/* ADR 031 — stock review before finishing */}
      <Modal
        isOpen={showStockReviewModal}
        onClose={() => setShowStockReviewModal(false)}
        title="Estoque em casa"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Atualize o que você tem em casa agora (sugestão = estoque anterior + comprado). Deixe
            em branco para não alterar.
          </p>
          {(sessionItems ?? [])
            .filter((i) => i.pickStatus === 'PICKED' && i.householdItemId)
            .map((item) => (
              <div key={item.id} className="flex items-center gap-2">
                <label className="flex-1 text-sm font-medium text-gray-700 truncate">
                  {item.itemNameSnapshot}
                </label>
                <input
                  type="text"
                  value={stockInputs[item.id] ?? ''}
                  onChange={(e) =>
                    setStockInputs((prev) => ({ ...prev, [item.id]: e.target.value }))
                  }
                  className="w-28 h-11 px-3 rounded-xl border border-gray-300 text-base text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="qtd"
                  aria-label={`Estoque de ${item.itemNameSnapshot}`}
                />
              </div>
            ))}
          <Button
            variant="primary"
            fullWidth
            size="md"
            onClick={handleStockReviewConfirm}
            title="Confirmar estoque e finalizar"
            aria-label="Confirmar estoque e finalizar"
          >
            Confirmar e finalizar
          </Button>
          <button
            onClick={() => {
              setShowStockReviewModal(false);
              setShowFinishModal(true);
            }}
            className="w-full text-xs text-gray-400 py-1 hover:text-gray-600 touch-manipulation"
          >
            Pular esta etapa
          </button>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={() => {
          setShowCancelModal(false);
          cancelMutation.mutate();
        }}
        title="Cancelar compra"
        message="Cancelar esta sessão de compra? Os itens marcados serão perdidos."
        confirmLabel="Cancelar compra"
        confirmVariant="danger"
        loading={cancelMutation.isPending}
      />
    </div>
  );
}
